// Data-driven blocks of a meeting document (spec §3.2): lines-of-effort rows and the summary
// tiles. Computed live from Ops Hub data on every render — never typed by hand.
import { splitObjectiveTasks } from "../clients/clientLogic.js";
import { isPreSubmission } from "../proposals/stages.js";
import { localDate } from "./period.js";
import { clientVisibleWork } from "./privacy.js";

export const TILE_LABELS = {
  tasksCompleted: "tasks completed this period",
  proposalsInPipeline: "proposals in pipeline",
  proposalsSubmitted: "proposals submitted",
  daysToNextDue: "days to next submission",
  decisionsNeeded: "decisions needed",
};
// Submitted and every outcome a submitted proposal can reach (Not Pursuing never was submitted).
const SUBMITTED_STAGES = new Set(["submitted", "selected", "funded", "not_selected"]);
const day = (iso) => (iso ? String(iso).slice(0, 10) : null);
// completedAt is a UTC timestamp; compare the user's local calendar day, not the UTC one.
const localDay = (v) => {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : localDate(d);
};
const inPeriod = (iso, start, end) => { const d = localDay(iso); return !!d && d >= start && d <= end; };

export function computeLoe(allObjectives, allTasks, { projectId, periodStart, periodEnd, hiddenObjectiveIds = [] }) {
  const hidden = new Set(hiddenObjectiveIds);
  const { objectives, tasks } = clientVisibleWork(allObjectives, allTasks);
  return objectives
    .filter((o) => !hidden.has(o.id) && (!projectId || o.projectId === projectId))
    .map((o) => {
      const its = tasks.filter((t) => t.objectiveId === o.id);
      const { open, completed } = splitObjectiveTasks(its);
      const total = its.length;
      return {
        id: o.id,
        name: o.name,
        done: completed.length,
        total,
        pct: total ? Math.round((completed.length / total) * 100) : 0,
        doneInPeriod: completed.filter((t) => inPeriod(t.completedAt, periodStart, periodEnd)).map((t) => t.name).slice(0, 5),
        upNext: open.slice(0, 3).map((t) => ({ name: t.name, dueDate: t.dueDate || null })),
      };
    });
}

// How many of this meeting's lines of effort are hidden (ignores ids of deleted objectives,
// objectives from other projects, and privacy-hidden objectives — those are never offered to
// the client view — so the "Hidden: N" line never counts ghosts).
export function countHiddenObjectives(objectives, { projectId, hiddenObjectiveIds = [], tasks = [] }) {
  const hidden = new Set(hiddenObjectiveIds);
  return clientVisibleWork(objectives, tasks).objectives.filter((o) => hidden.has(o.id) && (!projectId || o.projectId === projectId)).length;
}

export function computeTiles({ tasks: allTasks, objectives = [], proposals, sections, periodStart, periodEnd, today, hiddenTiles = [] }) {
  const hidden = new Set(hiddenTiles);
  const { tasks } = clientVisibleWork(objectives, allTasks);
  const upcoming = (proposals || [])
    .filter((p) => isPreSubmission(p.stage) && day(p.dueDate) && day(p.dueDate) >= today)
    .map((p) => Math.round((Date.parse(`${day(p.dueDate)}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000));
  const values = {
    tasksCompleted: tasks.filter((t) => t.status === "Completed" && inPeriod(t.completedAt, periodStart, periodEnd)).length,
    proposalsInPipeline: (proposals || []).filter((p) => isPreSubmission(p.stage)).length,
    proposalsSubmitted: (proposals || []).filter((p) => SUBMITTED_STAGES.has(p.stage)).length,
    daysToNextDue: upcoming.length ? Math.min(...upcoming) : null,
    decisionsNeeded: (sections || []).reduce((n, s) => n + (s.items || []).filter((i) => i.decisionNeeded).length, 0),
  };
  return Object.keys(TILE_LABELS)
    .filter((key) => values[key] != null && !hidden.has(key))
    .map((key) => ({ key, value: values[key], label: TILE_LABELS[key], warn: key === "decisionsNeeded" && values[key] > 0 }));
}
